import { Box, Icon } from '@dtx-company/shared-components/src'
import { ColorButton, StyledLi, StyledUl } from './styles'
import { FlexboxProps, LayoutProps, SpaceProps } from 'styled-system'
import { FlowcodeColorOptionType } from '../../types/flowcode.types'
import { checkmark } from '../../assets'
import { useGatedBuilderColors } from '../../hooks/useGatedBuilderColors'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import React, { useMemo } from 'react'

export type ColorSelectorProps = {
  activeColor: FlowcodeColorOptionType
  setActiveColor: (color: FlowcodeColorOptionType) => void
  enabled?: boolean
  onClick?: () => void
  useGatedColors?: boolean
  customColorSet?: FlowcodeColorOptionType[]
  showCheckmark?: boolean
  defaultColorId?: string | null
} & LayoutProps &
  SpaceProps &
  FlexboxProps

export const ColorSelector: React.FC<ColorSelectorProps> = ({
  activeColor,
  setActiveColor,
  enabled = true,
  useGatedColors,
  customColorSet,
  showCheckmark = false,
  defaultColorId,
  onClick,
  ...rest
}: ColorSelectorProps): JSX.Element => {
  const { availableColors, gatedColors } = useGatedBuilderColors()
  const colors: FlowcodeColorOptionType[] = useMemo(() => {
    if (customColorSet && customColorSet.length) {
      return customColorSet
    } else {
      return useGatedColors ? gatedColors : availableColors
    }
  }, [availableColors, customColorSet, gatedColors, useGatedColors])

  useOnce(() => {
    if (!colors) return false
    const defaultColor = colors?.find(({ id }) => id === defaultColorId)
    defaultColor && setActiveColor(defaultColor)
  })

  return (
    <StyledUl
      data-testid="flowcode-color-selector"
      role="radiogroup"
      justifyContent="flex-start"
      {...rest}
    >
      {colors.map(color => {
        const isSelected = activeColor.id === color.id
        return (
          <StyledLi key={color.label} role="radio" aria-checked={isSelected}>
            <ColorButton
              data-heap-id={`ColorSelector${color.label}_Button`}
              colorVariant="invisible"
              width="48px"
              aria-label={color.label}
              border="3px solid"
              borderColor={isSelected ? 'primary.flowBlue' : 'transparent'}
              onClick={() => {
                enabled && setActiveColor(color)
                onClick?.()
              }}
              disabled={!enabled && color.defaultOnly}
              borderRadius="100%"
              selected={isSelected}
              height="48px"
              padding={0}
              data-testid={color.label}
              title={color.label}
              margin="5px"
              backgroundColor={color.color}
              type="button"
            />
            {/* This allows the parent to still be clickable when this button is disabled. */}
            {!enabled && <Box position={'absolute'} width={'100%'} height={'100%'} />}
            {showCheckmark && color.label === activeColor.label && (
              <Icon src={checkmark} position="absolute" top="22px" left="20px" alt="" />
            )}
          </StyledLi>
        )
      })}
    </StyledUl>
  )
}
